import { useMemo, useState, useEffect, useRef, useCallback, type CSSProperties, type MouseEvent } from 'react';
import { CarouselZoom, type CarouselAnalyticsEvent, type MediaItem } from 'react-carousel-zoom';
import 'react-carousel-zoom/styles.css';
import './ProductImageGallery.css';
import { useIsDesktop } from '@/core/hooks/useMediaQuery';
import { WishlistIcon } from '@/shared/icons/icons';
import { NativeShareButton } from '@/shared/widgets/NativeShareButton';
import { TAILWIND_MD_MAX_PX } from '@/shared/constants/breakpoints';
import { cn } from '@/shared/utils/cn';

const DESKTOP_THUMB_SIZE = 88;
const DESKTOP_VISIBLE_THUMBS = 5;
const DESKTOP_CAROUSEL_MAX_SIZE = 512;
const DESKTOP_THUMB_NAV_INSET = 28;
const DESKTOP_THUMB_GAP =
  (DESKTOP_CAROUSEL_MAX_SIZE - DESKTOP_VISIBLE_THUMBS * DESKTOP_THUMB_SIZE) /
  (DESKTOP_VISIBLE_THUMBS - 1);

interface CarouselSeqItem {
  type: 'image' | 'video';
  urlMap: Record<string, string>;
}

interface MediaItems {
  carouselSeq?: CarouselSeqItem[];
}

interface ProductImageGalleryProps {
  mediaItems: Record<string, unknown>;
  designName: string;
  designId: number;
  isWishlisted: boolean;
  onAddToWishlist: () => void;
  onRemoveFromWishlist: () => void;
  shareUrl?: string | null;
  onMediaIndexChange?: (index: number) => void;
  onMediaThumbnailClick?: (index: number) => void;
  onMediaThumbnailScroll?: () => void;
  onVideoPlay?: (index: number) => void;
  onShareClick?: () => void;
}

function parseMediaItems(raw: Record<string, unknown>): CarouselSeqItem[] {
  const items = raw as MediaItems;
  return items.carouselSeq ?? [];
}

function getFirstHttpUrl(urlMap: Record<string, string>): string {
  return Object.values(urlMap).find((v) => typeof v === 'string' && v.startsWith('http')) ?? '';
}

function getThumbUrl(item: CarouselSeqItem): string {
  return item.urlMap['70'] ?? item.urlMap['poster_url'] ?? getFirstHttpUrl(item.urlMap);
}

function getMainUrl(item: CarouselSeqItem): string {
  return item.urlMap['1024'] ?? item.urlMap['828'] ?? item.urlMap['70'] ?? getFirstHttpUrl(item.urlMap);
}

function buildSrcSet(urlMap: Record<string, string>): string | undefined {
  const entries = Object.entries(urlMap)
    .filter(([key, url]) => /^\d+$/.test(key) && typeof url === 'string' && url.startsWith('http'))
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([width, url]) => `${url} ${width}w`);

  return entries.length > 0 ? entries.join(', ') : undefined;
}

function mapToCarouselItems(slides: CarouselSeqItem[], designName: string): MediaItem[] {
  return slides.map((item, index) => {
    const alt = `${designName} - ${item.type === 'image' ? 'image' : 'video'} ${index + 1}`;

    if (item.type === 'video') {
      const poster = item.urlMap['poster_url'] ?? getMainUrl(item);
      return {
        id: String(index),
        type: 'video',
        src: poster,
        poster,
        videoSrc: item.urlMap['video_url'],
        thumbnailSrc: getThumbUrl(item),
        alt,
      };
    }

    return {
      id: String(index),
      type: 'image',
      src: getMainUrl(item),
      srcSet: buildSrcSet(item.urlMap),
      sizes: `(max-width: ${TAILWIND_MD_MAX_PX}px) 100vw, ${DESKTOP_CAROUSEL_MAX_SIZE * 2}px`,
      thumbnailSrc: getThumbUrl(item),
      alt,
    };
  });
}

export function ProductImageGallery({
  mediaItems,
  designName,
  designId: _designId,
  isWishlisted,
  onAddToWishlist,
  onRemoveFromWishlist,
  shareUrl,
  onMediaIndexChange,
  onMediaThumbnailClick,
  onMediaThumbnailScroll,
  onVideoPlay,
  onShareClick,
}: ProductImageGalleryProps) {
  const slides = useMemo(() => parseMediaItems(mediaItems), [mediaItems]);
  const carouselItems = useMemo(
    () => mapToCarouselItems(slides, designName),
    [slides, designName]
  );

  const handleWishlistToggle = (e: MouseEvent) => {
    e.stopPropagation();
    if (isWishlisted) {
      onRemoveFromWishlist();
    } else {
      onAddToWishlist();
    }
  };


  const isDesktop = useIsDesktop();
  const [activeIndex, setActiveIndex] = useState(0);
  const galleryRef = useRef<HTMLDivElement>(null);
  const scrollTrackedRef = useRef(false);

  const handleIndexChange = useCallback((index: number) => {
    setActiveIndex(index);
  }, []);

  const handleCarouselAnalytics = useCallback(
    (event: CarouselAnalyticsEvent) => {
      if (event.type === 'thumb_click') {
        onMediaThumbnailClick?.(event.index);
        return;
      }
      if (event.type === 'swipe') {
        onMediaIndexChange?.(event.index);
      }
    },
    [onMediaThumbnailClick, onMediaIndexChange],
  );

  useEffect(() => {
    if (!onMediaThumbnailScroll || !galleryRef.current) return;

    const thumbStrip = galleryRef.current.querySelector('.rcz-thumbnails');
    if (!thumbStrip) return;

    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const handleScroll = () => {
      if (scrollTrackedRef.current) return;
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        scrollTrackedRef.current = true;
        onMediaThumbnailScroll();
      }, 1000);
    };

    thumbStrip.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      thumbStrip.removeEventListener('scroll', handleScroll);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [onMediaThumbnailScroll, carouselItems.length]);

  useEffect(() => {
    scrollTrackedRef.current = false;
  }, [mediaItems]);

  useEffect(() => {
    if (!onVideoPlay || !galleryRef.current) return;

    const attachPlayListener = (video: HTMLVideoElement) => {
      const handler = () => onVideoPlay(activeIndex);
      video.addEventListener('play', handler);
      return () => video.removeEventListener('play', handler);
    };

    const video = galleryRef.current.querySelector('video.rcz-main-video');
    if (!(video instanceof HTMLVideoElement)) return undefined;
    return attachPlayListener(video);
  }, [activeIndex, onVideoPlay, carouselItems.length]);

  const loop = carouselItems.length > 1;
  const hasThumbNav = isDesktop && loop;
  const canGoPrev = hasThumbNav && (loop || activeIndex > 0);
  const canGoNext = hasThumbNav && (loop || activeIndex < carouselItems.length - 1);

  const goToPrevSlide = () => {
    if (carouselItems.length <= 1) return;
    const nextIndex = loop
      ? (activeIndex - 1 + carouselItems.length) % carouselItems.length
      : Math.max(0, activeIndex - 1);
    handleIndexChange(nextIndex);
    onMediaIndexChange?.(nextIndex);
  };

  const goToNextSlide = () => {
    if (carouselItems.length <= 1) return;
    const nextIndex = loop
      ? (activeIndex + 1) % carouselItems.length
      : Math.min(carouselItems.length - 1, activeIndex + 1);
    handleIndexChange(nextIndex);
    onMediaIndexChange?.(nextIndex);
  };

  if (carouselItems.length === 0) return null;

  return (
    <div
      ref={galleryRef}
      className={cn(
        'pdp-gallery relative w-full md:mx-auto',
        hasThumbNav ? 'pdp-gallery--desktop-thumbs' : 'md:max-w-[512px]'
      )}
      style={
        isDesktop
          ? ({
              '--pdp-carousel-size': `${DESKTOP_CAROUSEL_MAX_SIZE}px`,
              '--pdp-thumb-nav-offset': hasThumbNav ? `${DESKTOP_THUMB_NAV_INSET}px` : '0px',
              ...(hasThumbNav
                ? {
                    '--pdp-thumb-size': `${DESKTOP_THUMB_SIZE}px`,
                    '--pdp-thumb-gap': `${DESKTOP_THUMB_GAP}px`,
                    '--pdp-visible-thumbs': DESKTOP_VISIBLE_THUMBS,
                    '--pdp-thumb-nav-inset': `${DESKTOP_THUMB_NAV_INSET}px`,
                  }
                : {}),
            } as CSSProperties)
          : undefined
      }
    >
      <CarouselZoom
        items={carouselItems}
        showThumbnails
        showNavigation={false}
        activeIndex={activeIndex}
        onIndexChange={handleIndexChange}
        onAnalytics={handleCarouselAnalytics}
        thumbnailSize={isDesktop ? DESKTOP_THUMB_SIZE : undefined}
        videoControls={false}
        loop={loop}
        aspectRatio={1}
        size={{ width: '100%' }}
        mediaFit="cover"
        zoomScale={2}
        autoSlide={false}
        autoSlideInterval={3000}
        fullscreenOnImageClick={{ desktop: false, mobile: true }}
        mobileMaxWidth={TAILWIND_MD_MAX_PX}
        fullscreenBackground="#f0f0f0"
        className="pdp-carousel w-full"
        zoomOnHover={true}
      />
      {hasThumbNav && (
        <div className="pdp-thumb-nav">
          <button
            type="button"
            className="pdp-thumb-nav__btn"
            onClick={goToPrevSlide}
            disabled={!canGoPrev}
            aria-label="Previous image"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <button
            type="button"
            className="pdp-thumb-nav__btn"
            onClick={goToNextSlide}
            disabled={!canGoNext}
            aria-label="Next image"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        </div>
      )}
      <div className="pdp-gallery__share-btn-area pointer-events-none md:hidden">
        <NativeShareButton
          title={designName}
          url={shareUrl}
          className="pdp-gallery__share-btn pointer-events-auto"
          onShareClick={onShareClick}
        />
      </div>
      <div className="pdp-gallery__wishlist-hit-area pointer-events-none">
        <button
          type="button"
          aria-label={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
          onClick={handleWishlistToggle}
          className={cn(
            'pdp-gallery__wishlist-btn pointer-events-auto',
            'flex h-[34px] w-[34px] items-center justify-center rounded-full border border-[#e2e2e2] bg-white',
            'shadow-[0_0_3px_0_rgba(25,25,25,0.15)]'
          )}
        >
          <WishlistIcon
            filled={isWishlisted}
            className={cn('h-[18px] w-[18px]', isWishlisted ? 'text-red-500' : 'text-[#959595]')}
          />
        </button>
      </div>
    </div>
  );
}
